import { alpha } from '@mui/material/styles';
import { colors } from '@munifocus/tokens';
import { palette } from './palette';

/**
 * Themed scrollbars for scroll containers (`overflow: auto`) across the web
 * products. The browser default is a chunky light-grey bar that reads as a
 * foreign element on both the deepTeal rail and our cream/white cards.
 *
 * Two variants — pick by the surface the container sits on. Spread into an
 * `sx` prop alongside the container's own overflow rules:
 *
 *     sx={{ overflowY: 'auto', ...darkScrollbar }}
 *
 * Firefox honours the standard `scrollbarWidth`/`scrollbarColor`; WebKit and
 * Blink take the `::-webkit-scrollbar-*` pseudo-elements. macOS overlay
 * scrollbars ignore both until the user opts into always-visible bars, which is
 * exactly the case these styles exist for.
 */

const THICKNESS = 6;

const makeScrollbar = (thumb: string, thumbHover: string) => ({
  scrollbarWidth: 'thin' as const,
  scrollbarColor: `${thumb} transparent`,
  '&::-webkit-scrollbar': {
    width: THICKNESS,
    height: THICKNESS,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: thumb,
    borderRadius: THICKNESS / 2,
    transition: 'background-color 200ms',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: thumbHover,
  },
});

/** For containers on deepTeal / dark surfaces — the sidenav rail, dark panels. */
export const darkScrollbar = makeScrollbar(palette.border.onDarkMuted, palette.text.onDarkGhost);

/** For containers on cream / white surfaces — scrollable cards, message lists. */
export const lightScrollbar = makeScrollbar(
  alpha(colors.common.black, 0.18),
  alpha(colors.common.black, 0.32),
);

/**
 * A scrollbar that stays out of sight until it is wanted: a thin thumb on a
 * transparent track, shown while the pointer is over the container or focus is
 * inside it, hidden at rest. For the portal rails, where a permanent bar is a
 * stripe down the chrome and the list is usually short enough not to need one.
 *
 * Firefox and Chromium 121+ take the standard `scrollbar-color`, so the reveal
 * is a colour change on hover; Safari takes the `::-webkit-scrollbar` thumb.
 * Keyboard users get the bar the moment focus enters, and touch devices scroll
 * with their own overlay bar regardless.
 *
 *     sx={{ overflowY: 'auto', ...quietScrollbar(true) }}
 */
export function quietScrollbar(onDark: boolean) {
  // The thumb is the on-dark subtle tone, softened: the bar is a hint of
  // position, not a control competing with the nav labels beside it.
  const rest = onDark ? alpha(palette.text.onDarkSubtle, 0.4) : alpha(colors.common.black, 0.22);
  const thumbHover = onDark ? palette.text.onDarkSubtle : alpha(colors.common.black, 0.36);
  return {
    scrollbarWidth: 'thin' as const,
    scrollbarColor: 'transparent transparent',
    transition: 'scrollbar-color 200ms',
    '&:hover, &:focus-within': { scrollbarColor: `${rest} transparent` },
    '&::-webkit-scrollbar': { width: THICKNESS, height: THICKNESS },
    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'transparent',
      borderRadius: THICKNESS / 2,
    },
    '&:hover::-webkit-scrollbar-thumb, &:focus-within::-webkit-scrollbar-thumb': {
      backgroundColor: rest,
    },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: thumbHover },
  };
}
